import axios from 'axios';
import config from '@/config/constants';
import { auth } from '@/config/firebase';

// Deployment Tracker (super-user). Talks to the super_admin/deployment/* API.
// Carries the Kiey JWT (Authorization) when logged in AND the Firebase ID
// token (X-Firebase-Token) when available, so the tool works both from the
// post-login dashboard and the pre-login super-user menu (same dual-auth the
// backend require_super_user accepts). The global super-admin axios
// interceptor adds the destructive passphrase on delete.

async function authHeaders() {
  const headers = {};
  const token = localStorage.getItem('auth_token');
  if (token) headers.Authorization = token;
  try {
    const fbUser = auth.currentUser;
    if (fbUser) headers['X-Firebase-Token'] = await fbUser.getIdToken();
  } catch (_) { /* no firebase session — JWT path only */ }
  return headers;
}

export default {
  namespaced: true,

  state: {
    teams: [],
    summary: null,
    feed: [],
    followups: [],
    manualOrder: [],
    status: 'idle', // idle | loading | loaded | error
    error: null,
    lastFetchedAt: null,
  },

  mutations: {
    SET_STATUS(state, s) { state.status = s; },
    SET_ERROR(state, e) { state.error = e; },
    SET_DATA(state, { rows, summary, feed, followups, manual_order }) {
      state.teams = rows || [];
      state.summary = summary || null;
      state.feed = feed || [];
      state.followups = followups || [];
      state.manualOrder = manual_order || [];
      state.lastFetchedAt = Date.now();
    },
    UPSERT_TEAM(state, team) {
      if (!team || !team.id) return;
      const i = state.teams.findIndex((t) => t.id === team.id);
      if (i === -1) state.teams.push(team);
      else state.teams.splice(i, 1, team);
    },
    REMOVE_TEAM(state, id) {
      state.teams = state.teams.filter((t) => t.id !== id);
    },
    SET_MANUAL_ORDER(state, order) { state.manualOrder = order || []; },
  },

  actions: {
    async fetchTeams({ commit }, { includeArchived = false, silent = false, force = false } = {}) {
      if (!silent) commit('SET_STATUS', 'loading');
      commit('SET_ERROR', null);
      try {
        // `force` (the manual Refresh button) forces server-side auto-discovery;
        // normal/poll loads let the server debounce it (it's expensive).
        const params = {};
        if (includeArchived) params.include_archived = 'true';
        if (force) params.discover = 'true';
        const res = await axios.get(`${config.api_url}super_admin/deployment/teams`, {
          headers: await authHeaders(),
          params,
          timeout: 15000,
        });
        if (res.data && res.data.code === 'SUCCESS') {
          commit('SET_DATA', res.data.data);
          commit('SET_STATUS', 'loaded');
          return res.data.data;
        }
        commit('SET_STATUS', 'error');
        return null;
      } catch (e) {
        commit('SET_STATUS', 'error');
        commit('SET_ERROR', e.response?.data?.errors?.join(', ') || e.message);
        throw e;
      }
    },

    async createTeam(_ctx, payload) {
      const res = await axios.post(`${config.api_url}super_admin/deployment/teams`, payload, {
        headers: await authHeaders(),
      });
      return res.data?.data?.team || null;
    },

    async updateTeam({ commit }, { id, fields }) {
      const res = await axios.put(`${config.api_url}super_admin/deployment/teams/${id}`, fields, {
        headers: await authHeaders(),
      });
      const team = res.data?.data?.team || null;
      if (team) commit('UPSERT_TEAM', team);
      return team;
    },

    async syncCounts({ commit }, id) {
      const res = await axios.post(`${config.api_url}super_admin/deployment/teams/${id}/sync`, {}, {
        headers: await authHeaders(),
      });
      const team = res.data?.data?.team || null;
      if (team) commit('UPSERT_TEAM', team);
      return team;
    },

    async deleteTeam({ commit }, id) {
      await axios.delete(`${config.api_url}super_admin/deployment/teams/${id}/delete`, {
        headers: await authHeaders(),
      });
      commit('REMOVE_TEAM', id);
    },

    async fetchUpdates(_ctx, id) {
      const res = await axios.get(`${config.api_url}super_admin/deployment/teams/${id}/updates`, {
        headers: await authHeaders(),
      });
      return res.data?.data?.updates || [];
    },

    async addUpdate(_ctx, { id, text }) {
      const res = await axios.post(
        `${config.api_url}super_admin/deployment/teams/${id}/updates`,
        { text },
        { headers: await authHeaders() },
      );
      return res.data?.data?.update || null;
    },

    async editNote(_ctx, { id, noteId, text }) {
      const res = await axios.put(
        `${config.api_url}super_admin/deployment/teams/${id}/updates/${noteId}`,
        { text },
        { headers: await authHeaders() },
      );
      return res.data?.data?.note || null;
    },

    async deleteNote(_ctx, { id, noteId }) {
      await axios.delete(
        `${config.api_url}super_admin/deployment/teams/${id}/updates/${noteId}`,
        { headers: await authHeaders() },
      );
    },

    async setDailyStatus(_ctx, { id, status }) {
      await axios.post(
        `${config.api_url}super_admin/deployment/teams/${id}/daily_status`,
        { status },
        { headers: await authHeaders() },
      );
    },

    async searchTenants(_ctx, q) {
      const res = await axios.get(`${config.api_url}super_admin/deployment/tenants`, {
        headers: await authHeaders(),
        params: q ? { q } : {},
      });
      return res.data?.data?.rows || [];
    },

    async reorderTasks(_ctx, order) {
      await axios.post(`${config.api_url}super_admin/deployment/tasks/reorder`, { order }, {
        headers: await authHeaders(),
      });
    },

    async dismissTask(_ctx, key) {
      await axios.post(`${config.api_url}super_admin/deployment/tasks/dismiss`, { key }, {
        headers: await authHeaders(),
      });
    },

    async draftOutreach(_ctx, { id, channel = null, stepKey = null, subStep = null }) {
      const body = {};
      if (channel) body.channel = channel;
      if (stepKey) body.step_key = stepKey;
      if (subStep) body.sub_step = subStep;
      const res = await axios.post(`${config.api_url}super_admin/deployment/teams/${id}/draft_outreach`, body, {
        headers: await authHeaders(),
        timeout: 30000,
      });
      return res.data?.data || null;
    },

    async sendOutreach(_ctx, { id, to, subject, body, stepKey, attachmentUrls }) {
      const res = await axios.post(
        `${config.api_url}super_admin/deployment/teams/${id}/send_outreach`,
        { to, subject, body, step_key: stepKey || null, attachment_urls: attachmentUrls || [] },
        { headers: await authHeaders() },
      );
      return res.data;
    },

    async fetchUnrouted() {
      const res = await axios.get(`${config.api_url}super_admin/deployment/comms/unrouted`, {
        headers: await authHeaders(),
      });
      return res.data?.data?.comms || [];
    },
    async assignComm(_ctx, { commId, teamId }) {
      const res = await axios.post(
        `${config.api_url}super_admin/deployment/comms/${commId}/assign`,
        { team_id: teamId },
        { headers: await authHeaders() },
      );
      return res.data?.data || null;
    },
    async fetchInsights() {
      const res = await axios.get(`${config.api_url}super_admin/deployment/insights`, {
        headers: await authHeaders(),
      });
      return res.data?.data || null;
    },
    async fetchSettings() {
      const res = await axios.get(`${config.api_url}super_admin/deployment/settings`, {
        headers: await authHeaders(),
      });
      return res.data?.data || { signature: '', templates: [], attachments: [] };
    },
    async saveSettings(_ctx, payload) {
      const res = await axios.put(`${config.api_url}super_admin/deployment/settings`, payload, {
        headers: await authHeaders(),
      });
      return res.data?.data || null;
    },

    async fetchContacts(_ctx, id) {
      const res = await axios.get(`${config.api_url}super_admin/deployment/teams/${id}/contacts`, {
        headers: await authHeaders(),
      });
      return res.data?.data || { contacts: [] };
    },

    // Short, team-attributed sign-up link for ONE recipient, used to fill an
    // invite template's {{invite_url}} so the sign-up page pre-fills that person.
    async fetchInviteLink(_ctx, { id, phone, role }) {
      const res = await axios.get(`${config.api_url}super_admin/deployment/teams/${id}/invite_link`, {
        params: { phone, role },
        headers: await authHeaders(),
      });
      return res.data?.data?.url || '';
    },

    // Soft-dismiss an unrouted reply (junk / wrong number) so it leaves the tray.
    async dismissComm(_ctx, id) {
      await axios.post(`${config.api_url}super_admin/deployment/comms/${id}/dismiss`, {}, {
        headers: await authHeaders(),
      });
    },

    // Unified Action History feed (newest first). Optional teamId scopes it to
    // one account; otherwise it's every action across all teams.
    async fetchActivity(_ctx, { teamId, since, limit } = {}) {
      const params = {};
      if (teamId) params.team_id = teamId;
      if (since) params.since = since;
      if (limit) params.limit = limit;
      const res = await axios.get(`${config.api_url}super_admin/deployment/activity`, {
        params,
        headers: await authHeaders(),
      });
      return res.data?.data?.activity || [];
    },

    // Account-activity feed: what an account's own agent/vendor owners + admins
    // did in the app (captured by the audit middleware), with their contact info.
    async fetchUserActivity(_ctx, { teamId, since, category, limit } = {}) {
      const params = {};
      if (teamId) params.team_id = teamId;
      if (since) params.since = since;
      if (category) params.category = category;
      if (limit) params.limit = limit;
      const res = await axios.get(`${config.api_url}super_admin/deployment/user_activity`, {
        params,
        headers: await authHeaders(),
      });
      return res.data?.data?.activity || [];
    },

    // AI Activity feed: everything the Onboarding Autopilot sent + genuine
    // replies to it, across all teams (newest first). Grouped by day on the UI.
    async fetchAiActivity() {
      const res = await axios.get(`${config.api_url}super_admin/deployment/ai_activity`, {
        headers: await authHeaders(),
      });
      return res.data?.data || { items: [], summary: {} };
    },

    async markCommsRead(_ctx, id) {
      await axios.post(`${config.api_url}super_admin/deployment/teams/${id}/comms/read`, {}, {
        headers: await authHeaders(),
      });
    },

    async fetchAllComms() {
      const res = await axios.get(`${config.api_url}super_admin/deployment/comms`, {
        headers: await authHeaders(),
      });
      return res.data?.data?.comms || [];
    },

    async fetchComms(_ctx, id) {
      const res = await axios.get(`${config.api_url}super_admin/deployment/teams/${id}/comms`, {
        headers: await authHeaders(),
      });
      return res.data?.data?.comms || [];
    },

    async sendSms(_ctx, { id, to, body, stepKey }) {
      const res = await axios.post(
        `${config.api_url}super_admin/deployment/teams/${id}/send_sms`,
        { to, body, step_key: stepKey || null },
        { headers: await authHeaders() },
      );
      return res.data;
    },

    async addFollowup(_ctx, { id, title, due_date }) {
      const res = await axios.post(
        `${config.api_url}super_admin/deployment/teams/${id}/followups`,
        { title, due_date },
        { headers: await authHeaders() },
      );
      return res.data?.data?.followup || null;
    },

    async editFollowup(_ctx, { fid, title, due_date }) {
      await axios.put(`${config.api_url}super_admin/deployment/followups/${fid}`, { title, due_date }, {
        headers: await authHeaders(),
      });
    },

    async completeFollowup(_ctx, { fid, done = true }) {
      await axios.post(
        `${config.api_url}super_admin/deployment/followups/${fid}/complete`,
        { done },
        { headers: await authHeaders() },
      );
    },

    async fetchSteps() {
      const res = await axios.get(`${config.api_url}super_admin/deployment/steps`, {
        headers: await authHeaders(),
      });
      return res.data?.data || { steps: [], metrics: [] };
    },

    async saveSteps(_ctx, steps) {
      const res = await axios.put(`${config.api_url}super_admin/deployment/steps`, { steps }, {
        headers: await authHeaders(),
      });
      return res.data?.data?.steps || [];
    },

    async setStepState({ commit }, { id, stepKey, state, follow_up, sub_done }) {
      const body = {};
      if (state) body.state = state;
      if (follow_up) body.follow_up = follow_up;
      if (sub_done) body.sub_done = sub_done;
      const res = await axios.post(
        `${config.api_url}super_admin/deployment/teams/${id}/steps/${stepKey}/state`,
        body,
        { headers: await authHeaders() },
      );
      const team = res.data?.data?.team || null;
      if (team) commit('UPSERT_TEAM', team);
      return team;
    },
  },
};
